import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity, ArrowRight, BarChart3, CalendarDays, Check, CheckCircle2, Clock3,
  Database, FileCheck2, FileText, Globe2, Heart, History, Laptop, Linkedin,
  ListChecks, LockKeyhole, Mail, Menu, MessageSquare, Mic, Moon, Phone, Pill,
  PlayCircle, Search, Server, ShieldCheck, Stethoscope, UserRound, UserRoundCheck,
  Users, X, Youtube
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { iniciarTracker, track } from '../lib/tracker';
import './Login.css';

const ASSET = '/landing/';

const FEATURES = [
  [Users, 'Pacientes', 'Gestiona tus pacientes y toda su historia clínica.'],
  [FileCheck2, 'Triaje', 'Registra signos vitales y motivo de consulta.'],
  [Stethoscope, 'Consulta médica', 'Crea historias clínicas completas con IA.'],
  [Activity, 'IA de dictado', 'Transcribe y estructura tu consulta.'],
  [Search, 'Buscador CIE-10', 'Encuentra diagnósticos rápidamente.'],
  [Pill, 'Receta con marca propia', 'Genera recetas con tu logo y tus datos.'],
  [CalendarDays, 'Citas', 'Organiza y gestiona tu agenda de atención.'],
  [MessageSquare, 'Mensajería médico–paciente', 'Comunicación segura dentro de ClinIA.'],
  [BarChart3, 'Dashboard del médico', 'Visualiza tu actividad y estadísticas.'],
  [UserRound, 'Portal del paciente', 'Tus pacientes ven sus consultas y recetas.'],
  [History, 'Trazabilidad', 'Registro de actividad, versiones y auditoría.'],
];

const FAQS = [
  ['¿ClinIA reemplaza al médico?', 'No. ClinIA es un asistente: organiza y propone información, pero el médico siempre revisa, corrige y confirma.'],
  ['¿Qué tan precisa es la transcripción?', 'La precisión depende de la calidad del audio. El sistema permite revisar toda la transcripción antes de incorporarla a la historia clínica.'],
  ['¿Puedo editar la historia clínica antes de guardarla?', 'Sí. Cada sección se puede revisar y editar. ClinIA nunca guarda una propuesta clínica como decisión final sin la confirmación del médico.'],
  ['¿En qué especialidades puedo usar ClinIA?', 'El formulario actual es general y completo. Podemos adaptar el flujo a la especialidad y forma de trabajo de cada médico.'],
  ['¿Necesito instalar algo?', 'No. ClinIA funciona desde el navegador en computadora, tablet o celular.'],
];

function Modal({ children, onClose, className = '' }) {
  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className={className} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Cerrar"><X size={19} /></button>
        {children}
      </div>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [addingEntity, setAddingEntity] = useState(false);
  const [recipeOpen, setRecipeOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => iniciarTracker(), []);

  const closeMenu = () => setMenuOpen(false);
  const openContact = () => { setContactOpen(true); setMenuOpen(false); };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      track('login', result.role);
      toast.success('¡Bienvenido a ClinIA Perú!');
      navigate(result.role === 'superadmin' ? '/superadmin' : `/${result.role}`);
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <main className="clinia-landing">
      <header className="landing-header">
        <nav className="landing-shell landing-nav" aria-label="Navegación principal">
          <a className="landing-logo" href="#inicio" onClick={closeMenu} aria-label="ClinIA, ir al inicio">
            <img src={`${ASSET}clinia-logo.jpg`} alt="ClinIA" />
          </a>
          <div className={`landing-links ${menuOpen ? 'mobile-open' : ''}`}>
            <a href="#inicio" data-track="nav_inicio" onClick={closeMenu}>Inicio</a>
            <a href="#como-funciona" data-track="nav_como-funciona" onClick={closeMenu}>Cómo funciona</a>
            <a href="#funciones" data-track="nav_funciones" onClick={closeMenu}>Funciones</a>
            <a href="#beneficios" data-track="nav_beneficios" onClick={closeMenu}>Beneficios</a>
            <a href="#demo" data-track="nav_demo" onClick={closeMenu}>Demo</a>
            <a href="#contacto" data-track="nav_contacto" onClick={closeMenu}>Contacto</a>
          </div>
          <div className="nav-actions">
            <button className="landing-button ghost" data-track="login_abrir" onClick={() => setLoginOpen(true)}>
              <LockKeyhole size={16} /> Ingresar
            </button>
            <button className="landing-button primary" data-track="registro_header" onClick={() => setRegistrationOpen(true)}>Solicita tu prueba gratis <ArrowRight size={17} /></button>
            <button className="mobile-menu-button" onClick={() => setMenuOpen((v) => !v)} aria-label="Abrir menú" aria-expanded={menuOpen}>
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </nav>
      </header>

      <section className="hero" id="inicio">
        <div className="landing-shell hero-grid">
          <div className="hero-content">
            <p className="landing-eyebrow">Más tiempo con tu paciente</p>
            <h1>Habla con tu paciente. <strong>ClinIA</strong> escribe la historia clínica por ti.</h1>
            <p className="hero-copy">Graba, transcribe y estructura la consulta con inteligencia artificial, para que solo revises y confirmes. Menos tiempo tipeando, más tiempo con lo que realmente importa.</p>
            <div className="hero-actions">
              <button className="landing-button primary" data-track="registro_hero" onClick={() => setRegistrationOpen(true)}>Solicita tu prueba gratis <ArrowRight size={18} /></button>
              <a className="landing-button secondary" href="#demo"><PlayCircle size={19} /> Ver demo en 2 min</a>
            </div>
            <div className="hero-benefits">
              <div className="quick-benefit"><Clock3 size={27} /><span><b>Ahorra tiempo</b>en cada consulta</span></div>
              <div className="quick-benefit"><Heart size={27} /><span><b>Historia clínica</b>más completa</span></div>
              <div className="quick-benefit"><ShieldCheck size={27} /><span><b>Enfócate en</b>tu paciente</span></div>
            </div>
          </div>
          <div className="hero-visual" aria-label="Médica usando ClinIA durante una consulta">
            <img className="hero-photo" src={`${ASSET}hero-doctora-paciente.webp`} alt="Médica conversando con su paciente" fetchPriority="high" />
            <img className="hero-product" src={`${ASSET}interfaz-consulta.webp`} alt="Interfaz de consulta médica de ClinIA" />
            <div className="hero-float"><Mic size={17} /> IA documentando la consulta</div>
          </div>
        </div>
      </section>

      <section className="landing-section comparison" id="beneficios">
        <div className="landing-shell comparison-grid">
          <article className="comparison-card">
            <h2>Sin ClinIA</h2><p>Más tiempo frente a la computadora, menos tiempo con tu paciente.</p>
            <div className="comparison-body">
              <img src={`${ASSET}sin-clinia.webp`} alt="Médico agotado documentando de noche" loading="lazy" />
              <div className="comparison-list">
                {[[Moon,'Documentas tarde en la noche','Llevas trabajo a casa.'],[Laptop,'Miras más la pantalla que a tu paciente','La conversación se interrumpe.'],[FileText,'Notas incompletas o desordenadas','Información importante puede quedar fuera.']].map(([Icon,title,text]) => (
                  <div className="comparison-item" key={title}><span className="comparison-icon"><Icon size={19}/></span><div><h3>{title}</h3><p>{text}</p></div></div>
                ))}
              </div>
            </div>
          </article>
          <article className="comparison-card good">
            <h2>Con ClinIA</h2><p>Más tiempo con tu paciente, una práctica médica más fluida.</p>
            <div className="comparison-body">
              <img src={`${ASSET}con-clinia.webp`} alt="Médica conversando directamente con su paciente" loading="lazy" />
              <div className="comparison-list">
                {[[UserRoundCheck,'Te enfocas en tu paciente','Conversaciones más humanas y de mayor calidad.'],[ListChecks,'Historia clínica organizada','Clara, completa y estructurada.'],[CheckCircle2,'Documentación más rápida','Solo revisas y confirmas.']].map(([Icon,title,text]) => (
                  <div className="comparison-item" key={title}><span className="comparison-icon"><Icon size={19}/></span><div><h3>{title}</h3><p>{text}</p></div></div>
                ))}
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="landing-section steps" id="como-funciona">
        <div className="landing-shell">
          <div className="section-heading"><div><p className="landing-eyebrow">Así de simple</p><h2 className="landing-title">Cómo funciona <span style={{color:'#0877ed'}}>ClinIA</span></h2></div><p className="landing-copy">Convierte tu conversación en una historia clínica completa en solo unos pasos.</p></div>
          <div className="step-grid">
            {[[Mic,'Habla','Graba la consulta de forma natural, como siempre hablas con tu paciente.'],[FileText,'Transcribe','ClinIA convierte el audio en texto con alta precisión médica.'],[ListChecks,'Organiza','La IA estructura la información en una historia clínica completa y ordenada.'],[Check,'Confirma','Solo revisas, ajustas si es necesario y guardas.']].map(([Icon,title,text]) => (
              <article className="step-card" key={title}><span className="step-icon"><Icon size={30}/></span><h3>{title}</h3><p>{text}</p></article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" id="funciones">
        <div className="landing-shell">
          <div className="section-heading"><div><p className="landing-eyebrow">Todo lo que necesitas</p><h2 className="landing-title">Funciones principales</h2></div><p className="landing-copy">Una plataforma completa para tu práctica médica, diseñada junto a médicos y mejorada con su uso real.</p></div>
          <div className="features-grid">
            {FEATURES.map(([Icon,title,text]) => <article className="feature-card" key={title}><span className="feature-icon"><Icon size={25}/></span><div><h3>{title}</h3><p>{text}</p></div></article>)}
          </div>
        </div>
      </section>

      <section className="landing-section prescription" id="demo">
        <div className="landing-shell prescription-banner">
          <div className="prescription-content">
            <p className="landing-eyebrow">Tu identidad en cada receta</p>
            <h2 className="landing-title">Receta con tu marca</h2>
            <p className="landing-copy">Genera recetas profesionales con el logo y datos de tu consultorio. Incluye diagnóstico CIE-10, indicaciones y una presentación limpia, lista para compartir o imprimir.</p>
            <button className="landing-button primary" data-track="ver_receta" onClick={() => setRecipeOpen(true)}>Ver ejemplo de receta <ArrowRight size={17}/></button>
          </div>
          <img className="prescription-image" src={`${ASSET}receta-ejemplo.webp`} alt="Ejemplo de receta médica personalizada en ClinIA" loading="lazy" />
        </div>
      </section>

      <section className="landing-section numbers">
        <div className="landing-shell">
          <div className="section-heading"><div><p className="landing-eyebrow">Uso real, resultados reales</p><h2 className="landing-title">ClinIA en números</h2></div><p className="landing-copy">Ya está en producción y es utilizado por médicos con pacientes reales en su práctica diaria.</p></div>
          <div className="number-grid">
            <div className="number-card wide"><span className="number-icon"><Server/></span><div><strong>En producción</strong><span>Plataforma activa y en uso real</span></div></div>
            <div className="number-card"><span className="number-icon"><Stethoscope/></span><div><strong>8</strong><span>médicos registrados</span></div></div>
            <div className="number-card"><span className="number-icon"><Users/></span><div><strong>~40</strong><span>pacientes</span></div></div>
            <div className="number-card"><span className="number-icon"><Database/></span><div><strong>~60</strong><span>consultas</span></div></div>
            <div className="number-card wide"><span className="number-icon"><Activity/></span><div><strong>Uso real en gastroenterología</strong><span>Validado en consulta ambulatoria</span></div></div>
          </div>
        </div>
      </section>

      <section className="landing-section faq">
        <div className="landing-shell">
          <div className="section-heading"><div><p className="landing-eyebrow">Preguntas frecuentes</p><h2 className="landing-title">¿Tienes dudas?</h2></div><p className="landing-copy">Aquí respondemos las consultas más comunes sobre ClinIA.</p></div>
          <div className="faq-grid">
            {FAQS.map(([question,answer], index) => <article className="faq-item" key={question}><button className="faq-question" data-track={`faq_${index + 1}`} onClick={() => setOpenFaq(openFaq === index ? null : index)} aria-expanded={openFaq === index}><span>{question}</span><span>{openFaq === index ? '−' : '+'}</span></button>{openFaq === index && <div className="faq-answer">{answer}</div>}</article>)}
          </div>
        </div>
      </section>

      <section className="final-cta" id="contacto">
        <div className="landing-shell cta-banner">
          <div><p className="landing-eyebrow">Da el primer paso</p><h2>Más tiempo con tus pacientes empieza hoy</h2><p>Conoce ClinIA y descubre cómo puede transformar tu práctica médica.</p></div>
          <button className="landing-button white" data-track="registro_final" onClick={() => setRegistrationOpen(true)}>Solicita tu prueba gratis <ArrowRight size={17}/></button>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-shell footer-row">
          <img src={`${ASSET}clinia-logo.jpg`} alt="ClinIA" />
          <p className="footer-tagline">Una práctica médica más humana,<br/>con el poder de la inteligencia artificial.</p>
          <div className="footer-contact">
            <a href="https://wa.me/51975486018" target="_blank" rel="noreferrer"><Phone size={17}/> +51 975 486 018</a>
            <a href="mailto:cliniaperu@gmail.com"><Mail size={17}/> cliniaperu@gmail.com</a>
            <a href="https://clinia.lat" target="_blank" rel="noreferrer"><Globe2 size={17}/> clinia.lat</a>
            <a href="https://www.linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn"><Linkedin size={17}/></a>
            <a href="https://www.youtube.com" target="_blank" rel="noreferrer" aria-label="YouTube"><Youtube size={18}/></a>
          </div>
        </div>
      </footer>

      {loginOpen && <Modal onClose={() => setLoginOpen(false)} className="modal-card">
        <img className="modal-brand" src={`${ASSET}clinia-logo.jpg`} alt="ClinIA" />
        <h2>Bienvenido de vuelta</h2><p className="modal-intro">Ingresa a tu espacio clínico seguro.</p>
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="field"><label htmlFor="email">Correo electrónico</label><input id="email" type="email" placeholder="usuario@ejemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="email-input" /></div>
          <div className="field"><label htmlFor="password">Contraseña</label><input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="password-input" /></div>
          <button className="landing-button primary" type="submit" disabled={loading} data-testid="login-button">{loading ? 'Iniciando sesión…' : 'Iniciar sesión'}</button>
        </form>
        <p className="modal-register">¿Eres paciente y no tienes cuenta? <Link to="/register">Regístrate aquí</Link></p>
      </Modal>}

      {contactOpen && <Modal onClose={() => setContactOpen(false)} className="modal-card contact-card">
        <img className="modal-brand" src={`${ASSET}clinia-logo.jpg`} alt="ClinIA" />
        <h2>Conversemos sobre tu consulta</h2><p className="modal-intro">Elige el canal que prefieras para solicitar una prueba o demostración de ClinIA.</p>
        <div className="contact-options">
          <a className="contact-option" href="https://wa.me/51975486018?text=Hola%2C%20quiero%20solicitar%20una%20prueba%20de%20ClinIA" target="_blank" rel="noreferrer"><Phone color="#0aaf64"/><div><b>WhatsApp</b><span>Respuesta directa del equipo</span></div></a>
          <a className="contact-option" href="mailto:cliniaperu@gmail.com?subject=Solicitud%20de%20prueba%20ClinIA"><Mail color="#0877ed"/><div><b>Correo electrónico</b><span>cliniaperu@gmail.com</span></div></a>
        </div>
      </Modal>}

      {registrationOpen && <Modal onClose={() => setRegistrationOpen(false)} className="modal-card registration-card">
        <img className="modal-brand" src={`${ASSET}clinia-logo.jpg`} alt="ClinIA" />
        <h2>Registro para médicos</h2><p className="modal-intro">Completa tus datos para solicitar acceso a ClinIA.</p>
        <form className="login-form" onSubmit={async (event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          try {
            const entity = [data.get('entity_type'), data.get('entity_name'), data.get('entity_city')].filter(Boolean).join(' · ');
            const { data: result } = await api.post('/auth/register-doctor', { full_name: data.get('full_name'), email: data.get('email'), password: data.get('password'), phone: data.get('phone'), specialization: data.get('specialization'), license_number: data.get('license_number'), requested_entity: entity || null });
            track('registro', data.get('entity_type'));
            toast.success(result.message);
            setRegistrationOpen(false);
          } catch (error) { const d = error.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'No se pudo enviar la solicitud'); }
        }}>
          <div className="form-grid">
            <div className="field"><label htmlFor="trial-name">Nombre completo</label><input id="trial-name" name="full_name" required placeholder="Dr. Juan Pérez" /></div>
            <div className="field"><label htmlFor="trial-email">Correo electrónico</label><input id="trial-email" name="email" type="email" required placeholder="doctor@ejemplo.com" /></div>
            <div className="field"><label htmlFor="trial-phone">Teléfono</label><input id="trial-phone" name="phone" required placeholder="999 999 999" /></div>
            <div className="field"><label htmlFor="trial-specialty">Especialidad</label><input id="trial-specialty" name="specialization" required placeholder="Medicina general" /></div>
          </div>
          <div className="form-grid"><div className="field"><label htmlFor="trial-license">Número de colegiatura (CMP)</label><input id="trial-license" name="license_number" required placeholder="CMP 12345" /></div><div className="field"><label htmlFor="trial-password">Contraseña</label><input id="trial-password" name="password" type="password" required minLength="8" placeholder="Mínimo 8 caracteres" /></div></div>
          <div className="field"><label htmlFor="trial-entity">Entidad a la que perteneces</label><select id="trial-entity" name="entity_type" required defaultValue=""><option value="" disabled>Selecciona una opción</option><option>Consultorio particular</option><option>Clínica o centro médico</option><option>Hospital</option></select></div>
          <button className="add-entity-button" type="button" onClick={() => setAddingEntity((value) => !value)}>+ Agregar entidad</button>
          {addingEntity && <div className="entity-fields"><div className="field"><label htmlFor="new-entity">Nombre de la entidad</label><input id="new-entity" name="entity_name" required placeholder="Nombre de clínica o consultorio" /></div><div className="field"><label htmlFor="new-entity-city">Ciudad o dirección</label><input id="new-entity-city" name="entity_city" placeholder="Lima, Perú" /></div></div>}
          <button className="landing-button primary" type="submit">Enviar solicitud <ArrowRight size={17} /></button>
        </form>
      </Modal>}

      {recipeOpen && <Modal onClose={() => setRecipeOpen(false)} className="lightbox-card"><img src={`${ASSET}receta-ejemplo.webp`} alt="Receta ClinIA ampliada" /></Modal>}
    </main>
  );
}
